/**
 * AlmanacRecordDetail — public reader for AI-generated weather-almanac
 * records at /almanac/records/:slug.
 *
 * Renders every field of the LLM-generated schema in a scannable
 * layout: hero + summary → key metrics → timeline → impacts →
 * facts / did-you-know → gallery → authoritative sources → community
 * contributions → Facebook-style discussion.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Calendar as CalendarIcon, Loader2, AlertTriangle,
  Cloud, CloudRain, Snowflake, Wind, Droplets, ThermometerSun, ImagePlus,
  Camera, Video, Link2, StickyNote, ExternalLink, Send, Newspaper, ShieldCheck,
  Book, Globe2, X, ImageIcon, Sparkles, MessageCircle,
} from "lucide-react";
import { http, resolveImage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import EntityMessageBoard from "@/components/EntityMessageBoard";
import ReportRichText from "@/components/ReportRichText";

const SEV_STYLES = {
  Minor:        "bg-green-100 text-green-800 border-green-200",
  Moderate:     "bg-yellow-100 text-yellow-800 border-yellow-200",
  Major:        "bg-orange-100 text-orange-800 border-orange-200",
  Severe:       "bg-red-100 text-red-800 border-red-200",
  Historic:     "bg-purple-100 text-purple-800 border-purple-200",
  Catastrophic: "bg-black text-white border-black",
};

/** Turn the shape "01:30 Local: The Meenachil River began overflowing…" into
 *  a two-column time+narrative row without regex misfires on missing colons. */
function splitTimeline(line) {
  const m = /^\s*(\d{1,2}:\d{2}(?:\s?(?:AM|PM|Local|IST|UTC))?)\s*[:\-–]\s*(.+)$/i.exec(line);
  return m ? [m[1], m[2]] : [null, line];
}

export default function AlmanacRecordDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await http.get(`/weather-almanac/records/${slug}`);
      setRec(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Emit schema.org WeatherEvent JSON-LD so search engines, academic
  // indexers, and Google Dataset Search can crawl these records.
  useEffect(() => {
    if (!rec) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": rec.title,
      "description": rec.summary || rec.description?.slice(0, 300),
      "startDate": rec.date,
      "eventStatus": "https://schema.org/EventScheduled",
      "location": {
        "@type": "Place",
        "name": rec.location_name || rec.city,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": rec.city || undefined,
          "addressRegion":   rec.state || rec.province || undefined,
          "addressCountry":  rec.country || undefined,
        },
        "geo": rec.latitude && rec.longitude ? {
          "@type": "GeoCoordinates",
          "latitude": rec.latitude,
          "longitude": rec.longitude,
        } : undefined,
      },
      "image": rec.featured_image || (rec.image_gallery || [])[0] || undefined,
      "keywords": [...(rec.tags || []), ...((rec.seo || {}).keywords || [])].join(", ") || undefined,
      "sameAs": rec.wikipedia || undefined,
      "license": rec.license || undefined,
    };
    // Strip undefineds so validators don't complain.
    const cleaned = JSON.parse(JSON.stringify(ld));
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = "almanac-jsonld";
    tag.textContent = JSON.stringify(cleaned);
    // Remove any prior tag so re-renders don't duplicate.
    document.getElementById("almanac-jsonld")?.remove();
    document.head.appendChild(tag);
    return () => tag.remove();
  }, [rec]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <Loader2 className="w-6 h-6 animate-spin opacity-60" />
      </div>
    );
  }
  if (err || !rec) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="wofy-card p-6 text-rose-700 border-rose-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {err || "Almanac record not found."}
        </div>
        <div className="mt-4">
          <Link to="/weather" className="wofy-btn-ghost text-sm"><ArrowLeft className="w-4 h-4" /> Back to weather</Link>
        </div>
      </div>
    );
  }

  const sev = rec.severity || "Moderate";
  const sevStyle = SEV_STYLES[sev] || "bg-neutral-100 text-neutral-800 border-neutral-200";
  const feat = rec.featured_image ? resolveImage(rec.featured_image) : null;
  const locBreadcrumb = [rec.city, rec.county, rec.state || rec.province, rec.country].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen" data-testid="almanac-record-detail">
      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-4">
        <Link to="/weather" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100" data-testid="almanac-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Weather Almanac
        </Link>
      </div>
      {feat ? (
        <div className="w-full relative" style={{ maxHeight: "420px", overflow: "hidden" }}>
          <img src={feat} alt="" className="w-full object-cover" style={{ maxHeight: "420px" }} />
        </div>
      ) : null}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-start gap-3">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${sevStyle}`} data-testid="almanac-severity">
            <AlertTriangle className="w-3 h-3" /> {sev}
          </span>
          {rec.event_type ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-white border">
              {rec.event_type}
            </span>
          ) : null}
          {rec.year ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-white border">
              <CalendarIcon className="w-3 h-3" /> {rec.date || rec.year}
            </span>
          ) : null}
          {locBreadcrumb ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-white border">
              <MapPin className="w-3 h-3" /> {locBreadcrumb}
            </span>
          ) : null}
        </div>

        <h1 className="mt-4 font-display text-3xl sm:text-4xl leading-tight" data-testid="almanac-title">
          {rec.title || "Weather Almanac Record"}
        </h1>
        {rec.summary ? (
          <p className="mt-3 text-lg opacity-90 max-w-3xl">{rec.summary}</p>
        ) : null}

        {/* Description */}
        {rec.description ? (
          <div className="wofy-card !p-6 mt-6" data-testid="almanac-description">
            <ReportRichText text={rec.description} className="opacity-90" />
          </div>
        ) : null}

        {/* Explore & Enhance — TOP banner. High-visibility CTA that
            invites everyone to enrich the record with their photos,
            videos, links, and observations. Duplicated at the bottom
            of the page for readers who scroll past the top. */}
        <ExploreEnhance
          recordId={rec.id}
          onAdded={load}
          user={user}
          location="top"
        />

        {/* Key metrics grid */}
        <MetricsGrid rec={rec} />

        {/* Timeline */}
        {rec.timeline?.length ? (
          <Section title="Timeline" icon={CalendarIcon} testid="almanac-timeline">
            <ol className="border-l pl-4 space-y-2">
              {rec.timeline.map((line, i) => {
                const [t, narrative] = splitTimeline(line);
                return (
                  <li key={i} className="pl-2">
                    {t ? <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-neutral-100 mr-2">{t}</span> : null}
                    <span className="text-sm">{narrative}</span>
                  </li>
                );
              })}
            </ol>
          </Section>
        ) : null}

        {/* Impacts */}
        {rec.impacts && Object.values(rec.impacts).some(Boolean) ? (
          <Section title="Impacts" icon={AlertTriangle} testid="almanac-impacts">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(rec.impacts).map(([k, v]) =>
                v ? (
                  <div key={k} className="wofy-card !p-4">
                    <div className="overline mb-1 text-xs" style={{ color: "var(--wofy-primary)" }}>{k.toUpperCase()}</div>
                    <p className="text-sm opacity-90">{v}</p>
                  </div>
                ) : null,
              )}
            </div>
          </Section>
        ) : null}

        {/* Facts */}
        {rec.facts?.length ? (
          <Section title="Facts" icon={Book} testid="almanac-facts">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 list-disc pl-5">
              {rec.facts.map((f, i) => <li key={i} className="text-sm">{f}</li>)}
            </ul>
          </Section>
        ) : null}

        {/* Did you know */}
        {rec.did_you_know?.length ? (
          <Section title="Did you know?" icon={Book} testid="almanac-did-you-know">
            <div className="space-y-2">
              {rec.did_you_know.map((f, i) => (
                <div key={i} className="wofy-card !p-3 text-sm">💡 {f}</div>
              ))}
            </div>
          </Section>
        ) : null}

        {/* Gallery */}
        {rec.image_gallery?.length ? (
          <Section title="Gallery" icon={ImageIcon} testid="almanac-gallery">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {rec.image_gallery.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded overflow-hidden">
                  <img src={u} alt="" className="w-full h-full object-cover hover:opacity-90 transition-opacity" loading="lazy" />
                </a>
              ))}
            </div>
          </Section>
        ) : null}

        {/* Sources */}
        <SourcesSection rec={rec} />

        {/* Tags */}
        {rec.tags?.length ? (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="almanac-tags">
            {rec.tags.map((t, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-full bg-white border">#{t}</span>
            ))}
          </div>
        ) : null}

        {/* Community contributions — bottom Explore & Enhance banner
            plus the contribution grid so readers landing here from a
            Google share still see the CTA before the discussion. */}
        <ExploreEnhance
          recordId={rec.id}
          onAdded={load}
          user={user}
          location="bottom"
        />
        <ContributionsList contributions={rec.contributions || []} />

        {/* Discussion */}
        <div className="mt-8">
          <EntityMessageBoard
            entityType="weather_almanac_record"
            entityId={rec.id}
            title="Discussion"
          />
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="mt-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 opacity-70" />
        <h2 className="font-display text-2xl">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function MetricsGrid({ rec }) {
  const t = rec.temperature || {};
  const p = rec.precipitation || {};
  const w = rec.wind || {};
  const rows = [
    { label: "High", val: t.high_c, unit: "°C", icon: ThermometerSun },
    { label: "Low",  val: t.low_c,  unit: "°C", icon: ThermometerSun },
    { label: "Rain", val: p.rain_mm, unit: "mm", icon: CloudRain },
    { label: "Snow", val: p.snow_cm, unit: "cm", icon: Snowflake },
    { label: "Wind gust", val: w.gust_kmh, unit: "km/h", icon: Wind },
    { label: "Pressure", val: rec.pressure_hpa, unit: "hPa", icon: Cloud },
    { label: "Humidity", val: p.humidity, unit: "%", icon: Droplets },
    { label: "Visibility", val: rec.visibility_km, unit: "km", icon: Cloud },
  ].filter((r) => r.val);
  if (!rows.length) return null;
  return (
    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="almanac-metrics">
      {rows.map(({ label, val, unit, icon: Icon }) => (
        <div key={label} className="wofy-card !p-3 text-center">
          <Icon className="w-4 h-4 mx-auto mb-1 opacity-60" />
          <div className="text-lg font-semibold">{val}<span className="text-xs opacity-60 ml-0.5">{unit}</span></div>
          <div className="text-[10px] uppercase tracking-widest opacity-60">{label}</div>
        </div>
      ))}
    </div>
  );
}

function SourcesSection({ rec }) {
  const cats = [
    { label: "Wikipedia", icon: Globe2, items: rec.wikipedia ? [rec.wikipedia] : [] },
    { label: "News",       icon: Newspaper,   items: rec.news_links       || [] },
    { label: "Government", icon: ShieldCheck, items: rec.government_links || [] },
    { label: "Weather",    icon: Cloud,       items: rec.weather_links    || [] },
    { label: "Research",   icon: Book,        items: rec.research_links   || [] },
    { label: "Videos",     icon: Video,       items: rec.video_links      || [] },
    { label: "Photos",     icon: ImageIcon,   items: rec.photo_links      || [] },
    { label: "Social",     icon: Globe2,      items: rec.social_media     || [] },
  ].filter((c) => c.items.length);
  if (!cats.length) return null;
  return (
    <Section title="Sources & references" icon={Newspaper} testid="almanac-sources">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cats.map((c) => (
          <div key={c.label} className="wofy-card !p-4">
            <div className="flex items-center gap-2 mb-2">
              <c.icon className="w-4 h-4 opacity-70" />
              <div className="font-semibold text-sm">{c.label}</div>
            </div>
            <ul className="space-y-1">
              {c.items.map((u, i) => (
                <li key={i} className="text-xs truncate">
                  <a href={u} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70" style={{ color: "var(--wofy-primary)" }}>
                    <ExternalLink className="w-3 h-3 inline mr-1" />
                    {u}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ContributionForm({ recordId, onAdded, disabled }) {
  const [kind, setKind] = useState("text");
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [notes, setNotes] = useState("");
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const readPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setPhotoDataUrl(r.result);
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { kind, caption: caption || null, notes: notes || null };
      if (kind === "photo") body.data_url = photoDataUrl;
      if (kind === "video_link" || kind === "website" || kind === "social_link") body.url = url;
      await http.post(`/weather-almanac/records/${recordId}/contributions`, body);
      toast.success("Thanks — your contribution is live.");
      setUrl(""); setCaption(""); setNotes(""); setPhotoDataUrl(null);
      if (fileRef.current) fileRef.current.value = "";
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit contribution.");
    } finally {
      setBusy(false);
    }
  };

  if (disabled) {
    return (
      <div className="wofy-card !p-4 mb-3 text-sm opacity-70">
        <Link to={`/login?next=/almanac/records/${window.location.pathname.split("/").pop()}`} className="underline">
          Sign in
        </Link>{" "}
        to contribute photos, videos, notes, or links.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="wofy-card !p-4 mb-3 space-y-2" data-testid="contribution-form">
      <div className="flex flex-wrap gap-2 text-xs">
        {[
          { k: "text",        label: "📝 Comment",       icon: StickyNote },
          { k: "photo",       label: "📷 Photo",         icon: Camera },
          { k: "video_link",  label: "🎬 Video link",    icon: Video },
          { k: "website",     label: "🔗 Website / news", icon: Link2 },
          { k: "social_link", label: "🌐 Social post",   icon: Globe2 },
        ].map(({ k, label }) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={`px-3 py-1.5 rounded-full border ${kind === k ? "bg-black text-white border-black" : "bg-white"}`}
            data-testid={`contrib-kind-${k}`}
          >
            {label}
          </button>
        ))}
      </div>
      {kind === "photo" ? (
        <input ref={fileRef} type="file" accept="image/*" onChange={readPhoto} className="text-sm" data-testid="contrib-photo-input" />
      ) : null}
      {kind === "video_link" || kind === "website" || kind === "social_link" ? (
        <input
          type="url"
          placeholder={
            kind === "video_link"  ? "YouTube / Vimeo / Facebook / Instagram URL…" :
            kind === "social_link" ? "https://x.com/… or https://facebook.com/… or Reddit / LinkedIn / TikTok / Threads / Mastodon / Bluesky" :
                                     "https://…"
          }
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="contrib-url"
        />
      ) : null}
      {kind !== "text" ? (
        <input
          type="text"
          placeholder="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          maxLength={300}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="contrib-caption"
        />
      ) : null}
      <textarea
        placeholder={kind === "text" ? "Share what you observed…" : "Notes (optional)…"}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        maxLength={2000}
        className="w-full rounded-lg border px-3 py-2 text-sm"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid="contrib-notes"
      />
      <div className="flex justify-end">
        <button type="submit" disabled={busy} className="wofy-btn-primary text-sm disabled:opacity-60" data-testid="contrib-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Sending…" : "Add contribution"}
        </button>
      </div>
    </form>
  );
}

/**
 * ExploreEnhance — the prominent "user action" banner that the user
 * asked for at both the top AND the bottom of every almanac record.
 * Same rich composer, wrapped in a high-visibility gradient card with
 * the exact tagline the user specified.
 */
function ExploreEnhance({ recordId, onAdded, user, location }) {
  return (
    <div
      className="mt-8 rounded-2xl overflow-hidden border relative"
      style={{
        borderColor: "var(--wofy-border)",
        background: "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(139,92,246,0.08))",
      }}
      data-testid={`explore-enhance-${location}`}
    >
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6" style={{ color: "var(--wofy-primary)" }} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-2xl leading-tight">Explore &amp; Enhance</h3>
            <p className="text-sm opacity-80 mt-0.5">
              Please add your photos and comments about this weather report.
            </p>
          </div>
        </div>
        <div className="mt-4">
          <ContributionForm recordId={recordId} onAdded={onAdded} disabled={!user} />
        </div>
      </div>
    </div>
  );
}

function ContributionsList({ contributions }) {
  if (!contributions?.length) {
    return <div className="text-sm opacity-60 py-3">No community contributions yet — be the first to add a photo, video, or observation.</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {contributions.map((c) => (
        <div key={c.id} className="wofy-card !p-3">
          {c.kind === "photo" && c.url ? (
            <img src={resolveImage(c.url)} alt="" className="w-full aspect-video object-cover rounded mb-2" loading="lazy" />
          ) : null}
          {c.kind === "video_link" && c.video?.embed_url ? (
            <div className="relative w-full mb-2" style={{ paddingTop: "56.25%" }}>
              <iframe src={c.video.embed_url} className="absolute inset-0 w-full h-full rounded" allowFullScreen title="video" loading="lazy" />
            </div>
          ) : null}
          {c.kind === "video_link" && !c.video?.embed_url && c.url ? (
            <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs underline">Open Instagram/video →</a>
          ) : null}
          {c.kind === "website" && c.url ? (
            <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs underline break-all">{c.url}</a>
          ) : null}
          {c.kind === "social_link" && c.url ? (
            <a
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs underline break-all"
              data-testid={`contrib-social-${c.id}`}
            >
              <span className="uppercase font-mono text-[10px] px-1.5 py-0.5 rounded bg-neutral-100">
                {c.platform || "SOCIAL"}
              </span>
              <span className="truncate">{c.url}</span>
            </a>
          ) : null}
          {c.caption ? <div className="text-sm font-medium">{c.caption}</div> : null}
          {c.notes ? <p className="text-xs opacity-80 mt-1 whitespace-pre-line">{c.notes}</p> : null}
          <div className="text-[10px] opacity-50 mt-2">
            by {c.user_name || c.user_handle || "Anonymous"} · {new Date(c.created_at).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}
